/** Shared formatting helpers. Keep all number/time formatting here so the UI is consistent. */

/** 75.3 -> "1:15", 3725 -> "1:02:05" (video timestamps). */
export function formatTimestamp(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** Human duration: 45 -> "45s", 125 -> "2m 5s", 7260 -> "2h 1m". */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return sec > 0 && m < 10 ? `${m}m ${sec}s` : `${m}m`;
}

/** 0.4567 -> "46%" (ratio in [0,1]). */
export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return "—";
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** 1284 -> "1,284"; 12900 -> "12.9K"; 4200000 -> "4.2M". */
export function formatCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

export function formatNumber(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en", { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);
}

/** Score in [0,100] -> "82". */
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined || Number.isNaN(score)) return "—";
  return Math.round(score).toString();
}

/** Playback speed 1.25 -> "1.25×". */
export function formatSpeed(speed: number | null | undefined): string {
  if (!speed) return "—";
  return `${Number(speed.toFixed(2))}×`;
}

/** 1 -> "1 learner", 3 -> "3 learners", 1200 -> "1,200 learners"; pass `plural` for irregular nouns. */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/** Remaining video time, rounded for glanceability: "Under a minute left", "7 min left", "1h 5m left". */
export function formatTimeLeft(seconds: number): string {
  if (seconds < 60) return "Under a minute left";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}h ${rest}m left` : `${hours}h left`;
}

/** "Ada", "Ada and Alan", "Ada, Alan and Grace", "Ada, Alan and 2 others". */
export function formatNameList(names: readonly string[], max = 3): string {
  const clean = names.map((name) => name.trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? "";
  if (clean.length <= max) return `${clean.slice(0, -1).join(", ")} and ${clean[clean.length - 1]}`;
  const shown = clean.slice(0, max - 1);
  return `${shown.join(", ")} and ${pluralize(clean.length - shown.length, "other")}`;
}

/** "desktop" -> "Desktop" (labels from the API are data; keep them readable). */
export function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

/** "Ada Lovelace" -> "AL" */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
